import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, ArrowLeft, Play, Loader2, LogOut, CheckCircle2 } from 'lucide-react';
import useLobby from '../../hooks/useLobby';
import PlayerSlot from './PlayerSlot';
import CountryPicker from './CountryPicker';

const GameLobby = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const {
    lobby, loading, error,
    fetchLobby, selectCountry, toggleReady, startGame, leaveLobby,
  } = useLobby(gameId);

  // Get current user_id from JWT
  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id;
    } catch {
      return null;
    }
  }, []);

  // Redirect when game starts (SSE + polling fallback)
  useEffect(() => {
    if (lobby?.status === 'in_progress') {
      navigate(`/multi/game/${gameId}`);
    }
    if (lobby?.status === 'finished') {
      navigate('/multi');
    }
  }, [lobby?.status, gameId, navigate]);

  // Polling fallback: 15s (SSE fait le temps réel)
  useEffect(() => {
    if (!gameId || lobby?.status === 'in_progress' || lobby?.status === 'finished') return;
    const interval = setInterval(() => {
      fetchLobby();
    }, 15000);
    return () => clearInterval(interval);
  }, [gameId, lobby?.status, fetchLobby]);

  const currentPlayer = lobby?.players?.find(p => p.user_id === currentUserId);
  const isHost = currentPlayer?.is_host;
  const isReady = currentPlayer?.is_ready;
  const hasCountry = !!currentPlayer?.country_short_name;

  const takenCountries = lobby?.players
    ?.filter(p => p.country_short_name)
    ?.map(p => p.country_short_name) || [];

  const allPlayersReady = lobby?.players?.length >= 2 &&
    lobby?.players?.every(p => p.is_ready);

  const copyGameCode = () => {
    if (lobby?.game_code) {
      navigator.clipboard.writeText(lobby.game_code);
    }
  };

  const handleLeave = async () => {
    await leaveLobby();
    navigate('/multi');
  };

  if (loading && !lobby) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!lobby) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Lobby introuvable'}</p>
          <button
            onClick={() => navigate('/multi')}
            className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const emptySlots = Math.max(0, (lobby.max_players || 4) - (lobby.players?.length || 0));

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/30 to-black" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLeave}
              className="p-2 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-700/50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-300" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Salle d'attente</h1>
              <p className="text-slate-400 text-sm">
                {lobby.players?.length || 0}/{lobby.max_players || 4} joueurs
              </p>
            </div>
          </div>

          {/* Game code */}
          <button
            onClick={copyGameCode}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800/80 border border-slate-600 hover:border-blue-400 transition-colors group"
          >
            <span className="font-mono font-bold text-lg text-blue-400 tracking-wider">
              {lobby.game_code}
            </span>
            <Copy className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="grid gap-6">
          {/* Players list */}
          <div className="p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700">
            <h2 className="text-lg font-bold text-white mb-4">Joueurs</h2>
            <div className="space-y-2">
              {lobby.players?.map((player) => (
                <PlayerSlot
                  key={player.username}
                  player={player}
                  isHost={player.is_host}
                  isCurrentUser={player.user_id === currentUserId}
                />
              ))}
              {Array.from({ length: emptySlots }).map((_, i) => (
                <PlayerSlot key={`empty-${i}`} player={null} />
              ))}
            </div>
          </div>

          {/* Country selection */}
          <div className="p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700">
            <h2 className="text-lg font-bold text-white mb-4">Choisissez votre pays</h2>
            <CountryPicker
              selectedCountry={currentPlayer?.country_short_name}
              onSelect={selectCountry}
              takenCountries={takenCountries}
              disabled={isReady}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleLeave}
              className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors flex items-center justify-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              Quitter
            </button>

            <button
              onClick={toggleReady}
              disabled={!hasCountry}
              className={`flex-1 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                isReady
                  ? 'bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30'
                  : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed'
              }`}
            >
              <CheckCircle2 className="w-5 h-5" />
              {isReady ? 'Prêt !' : 'Se mettre prêt'}
            </button>

            {isHost && (
              <button
                onClick={startGame}
                disabled={!allPlayersReady}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold transition-all disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Lancer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameLobby;
